import { RateLimiter, type RateLimiterOpts } from '../limiter';
import { backOff, type BackoffOptions } from 'exponential-backoff';

/**
 * Supported chains and their `chainId` for the SDK.
 * @enum
 */
export enum SupportedChainId {
  MAINNET = 1,
  GNOSIS_CHAIN = 100,
  ARBITRUM_ONE = 42161,
  SEPOLIA = 11155111,
}

export enum SigningScheme {
  EIP712 = 'eip712',
  ETHSIGN = 'ethsign',
  PRESIGN = 'presign',
  EIP1271 = 'eip1271',
}

export const COW_PROTOCOL_SETTLEMENT_CONTRACT_ADDRESS = {
  [SupportedChainId.GNOSIS_CHAIN]: '0x9008D19f58AAbD9eD0D60971565AA8510560ab41',
};
export const COW_PROTOCOL_VAULT_RELAYER_ADDRESS = {
  [SupportedChainId.GNOSIS_CHAIN]: '0xC92E8bdf79f0507f65a392b0ab4667716BFE0110',
};

type ApiBaseUrls = Record<SupportedChainId, string>;

interface ApiContext {
  chainId: SupportedChainId;
  env: CowEnv;
  baseUrls?: ApiBaseUrls;
  limiterOpts?: RateLimiterOpts;
  backoffOpts?: BackoffOptions;
}

type PartialApiContext = Partial<ApiContext> & {
  signal?: AbortSignal;
};

type CowEnv = 'prod' | 'staging';

const DEFAULT_COW_API_CONTEXT: ApiContext = {
  env: 'prod',
  chainId: SupportedChainId.MAINNET,
};

//

enum OrderKind {
  BUY = 'buy',
  SELL = 'sell',
}
enum SellTokenSource {
  ERC20 = 'erc20',
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}
enum BuyTokenDestination {
  ERC20 = 'erc20',
  INTERNAL = 'internal',
}
export enum PriceQuality {
  FAST = 'fast',
  OPTIMAL = 'optimal',
  VERIFIED = 'verified',
}
export enum OrderQuoteSideKindSell {
  SELL = 'sell',
}
enum OrderQuoteSideKindBuy {
  BUY = 'buy',
}
export enum OrderStatus {
  PREstring_PENDING = 'prestringPending',
  OPEN = 'open',
  FULFILLED = 'fulfilled',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
}
type EthflowData = {
  /**
   * Specifies in which transaction the order was refunded. If
   * this field is null the order was not yet refunded.
   *
   */
  refundTxHash: string | null;
  /**
   * Describes the `validTo` of an order ethflow order.
   *
   * **NOTE**: For ethflow orders, the `validTo` encoded in the smart
   * contract is `type(uint256).max`.
   *
   */
  userValidTo: number;
};
enum placementError {
  QUOTE_NOT_FOUND = 'QuoteNotFound',
  VALID_TO_TOO_FAR_IN_FUTURE = 'ValidToTooFarInFuture',
  PRE_VALIDATION_ERROR = 'PreValidationError',
}
enum OrderClass {
  MARKET = 'market',
  LIMIT = 'limit',
  LIQUIDITY = 'liquidity',
}
type OnchainOrderData = {
  /**
   * If orders are placed as on-chain orders, the owner of the order might
   * be a smart contract, but not the user placing the order. The
   * actual user will be provided in this field.
   *
   */
  sender: string;
  /**
   * Describes the error, if the order placement was not successful. This could
   * happen, for example, if the `validTo` is too high, or no valid quote was
   * found or generated.
   *
   */
  placementError?: placementError;
};
export type OrderCreation = {
  /**
   * see `OrderParameters::sellToken`
   */
  sellToken: string;
  /**
   * see `OrderParameters::buyToken`
   */
  buyToken: string;
  /**
   * see `OrderParameters::receiver`
   */
  receiver?: string | null;
  /**
   * see `OrderParameters::sellAmount`
   */
  sellAmount: string;
  /**
   * see `OrderParameters::buyAmount`
   */
  buyAmount: string;
  /**
   * see `OrderParameters::validTo`
   */
  validTo: number;
  /**
   * see `OrderParameters::feeAmount`
   */
  feeAmount: string;
  /**
   * see `OrderParameters::kind`
   */
  kind: OrderKind;
  /**
   * see `OrderParameters::partiallyFillable`
   */
  partiallyFillable: boolean;
  /**
   * see `OrderParameters::sellTokenBalance`
   */
  sellTokenBalance?: SellTokenSource;
  /**
   * see `OrderParameters::buyTokenBalance`
   */
  buyTokenBalance?: BuyTokenDestination;
  signingScheme: SigningScheme;
  signature: string;
  /**
   * If set, the backend enforces that this string matches what is decoded as the *signer* of
   * the string. This helps catch errors with invalid string encodings as the backend
   * might otherwise silently work with an unexpected string that for example does not have
   * any balance.
   *
   */
  from?: string | null;
  /**
   * Orders can optionally include a quote ID. This way the order can be linked to a quote
   * and enable providing more metadata when analysing order slippage.
   *
   */
  quoteId?: number | null;
  /**
   * This field comes in two forms for backward compatibility. The hash form will eventually
   * stop being accepted.
   *
   */
  appData: string | string;
  /**
   * May be set for debugging purposes. If set, this field is compared to what the backend
   * internally calculates as the app data hash based on the contents of `string`. If the
   * hash does not match, an error is returned. If this field is set, then `string` **MUST** be
   * a string encoding of a JSON object.
   *
   */
  appDataHash?: string | null;
};
type OrderMetaData = {
  /**
   * Creation time of the order. Encoded as ISO 8601 UTC.
   */
  creationDate: string;
  class: OrderClass;
  owner: string;
  uid: UID;
  /**
   * Unused field that is currently always set to `null` and will be removed in the future.
   *
   * @deprecated
   */
  availableBalance?: string | null;
  /**
   * The total amount of `sellToken` that has been executed for this order including fees.
   *
   */
  executedSellAmount: string;
  /**
   * The total amount of `sellToken` that has been executed for this order without fees.
   *
   */
  executedSellAmountBeforeFees: string;
  /**
   * The total amount of `buyToken` that has been executed for this order.
   *
   */
  executedBuyAmount: string;
  /**
   * The total amount of fees that have been executed for this order.
   */
  executedFeeAmount: string;
  /**
   * Has this order been invalidated?
   */
  invalidated: boolean;
  /**
   * Order status.
   */
  status: OrderStatus;
  /**
   * Amount that the signed fee would be without subsidies.
   */
  fullFeeAmount?: string;
  /**
   * Liquidity orders are functionally the same as normal smart contract orders but are not
   * placed with the intent of actively getting traded. Instead they facilitate the
   * trade of normal orders by allowing them to be matched against liquidity orders which
   * uses less gas and can have better prices than external liquidity.
   *
   * As such liquidity orders will only be used in order to improve settlement of normal
   * orders. They should not be expected to be traded otherwise and should not expect to get
   * surplus.
   *
   */
  isLiquidityOrder?: boolean;
  ethflowData?: EthflowData;
  /**
   * This represents the actual trader of an on-chain order.
   *
   * ### ethflow orders
   *
   * In this case, the `owner` would be the `EthFlow` contract and *not* the actual trader.
   *
   */
  onchainUser?: string;
  /**
   * There is some data only available for orders that are placed on-chain. This data
   * can be found in this object.
   *
   */
  onchainOrderData?: OnchainOrderData;
  /**
   * Surplus fee that the limit order was executed with.
   */
  executedSurplusFee?: string | null;
  /**
   * Full `string`, which the contract-level `string` is a hash of. See `OrderCreation`
   * for more information.
   *
   */
  fullstring?: string | null;
};
export type OrderParameters = {
  /**
   * ERC-20 token to be sold.
   */
  sellToken: string;
  /**
   * ERC-20 token to be bought.
   */
  buyToken: string;
  /**
   * An optional Ethereum string to receive the proceeds of the trade instead
   * of the owner (i.e. the order signer).
   *
   */
  receiver?: string | null;
  /**
   * Amount of `sellToken` to be sold in atoms.
   */
  sellAmount: string;
  /**
   * Amount of `buyToken` to be bought in atoms.
   */
  buyAmount: string;
  /**
   * Unix timestamp (`uint32`) until which the order is valid.
   */
  validTo: number;
  appData: string;
  /**
   * feeRatio * sellAmount + minimal_fee in atoms.
   */
  feeAmount: string;
  /**
   * The kind is either a buy or sell order.
   */
  kind: OrderKind;
  /**
   * Is the order fill-or-kill or partially fillable?
   */
  partiallyFillable: boolean;
  sellTokenBalance?: SellTokenSource;
  buyTokenBalance?: BuyTokenDestination;
  signingScheme?: SigningScheme;
};
type OrderQuoteSide =
  | {
      kind: OrderQuoteSideKindSell;
      /**
       * The total amount that is available for the order. From this value, the fee
       * is deducted and the buy amount is calculated.
       *
       */
      sellAmountBeforeFee: string;
    }
  | {
      kind: OrderQuoteSideKindSell;
      /**
       * The `sellAmount` for the order.
       */
      sellAmountAfterFee: string;
    }
  | {
      kind: OrderQuoteSideKindBuy;
      /**
       * The `buyAmount` for the order.
       */
      buyAmountAfterFee: string;
    };
type OrderQuoteValidity =
  | {
      /**
       * Unix timestamp (`uint32`) until which the order is valid.
       */
      validTo?: number;
    }
  | {
      /**
       * Number (`uint32`) of seconds that the order should be valid for.
       */
      validFor?: number;
    };
type OrderQuoteRequest = OrderQuoteSide &
  OrderQuoteValidity & {
    /**
     * ERC-20 token to be sold
     */
    sellToken: string;
    /**
     * ERC-20 token to be bought
     */
    buyToken: string;
    /**
     * An optional address to receive the proceeds of the trade instead of the
     * `owner` (i.e. the order signer).
     *
     */
    receiver?: string | null;
    /**
     * AppData which will be assigned to the order.
     * Expects either a string JSON doc as defined on [AppData](https://github.com/cowprotocol/app-data) or a
     * hex encoded string for backwards compatibility.
     * When the first format is used, it's possible to provide the derived appDataHash field.
     *
     */
    appData?: string;
    /**
     * The hash of the stringified JSON appData doc.
     * If present, `appData` field must be set with the aforementioned data where this hash is derived from.
     * In case they differ, the call will fail.
     *
     */
    appDataHash?: string;
    sellTokenBalance?: SellTokenSource;
    buyTokenBalance?: BuyTokenDestination;
    from: string;
    priceQuality?: PriceQuality;
    signingScheme?: SigningScheme;
    /**
     * Flag to signal whether the order is intended for on-chain order placement. Only valid
     * for non ECDSA-signed orders."
     *
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onchainOrder?: any;
  };
export type OrderQuoteResponse = {
  quote: OrderParameters;
  from?: string;
  /**
   * Expiration date of the offered fee. Order service might not accept
   * the fee after this expiration date. Encoded as ISO 8601 UTC.
   *
   */
  expiration: string;
  /**
   * Quote ID linked to a quote to enable providing more metadata when analysing
   * order slippage.
   *
   */
  id?: number;
  /**
   * Whether it was possible to verify that the quoted amounts are accurate using a simulation.
   *
   */
  verified: boolean;
};
type Order = OrderCreation & OrderMetaData;

type UID = string;

/**
 * Error thrown when the CoW Protocol OrderBook API returns an error.
 */
export class OrderBookApiError<T = unknown> extends Error {
  /**
   * Error thrown when the CoW Protocol OrderBook API returns an error.
   * @param response The response from the CoW Protocol OrderBook API.
   * @param body The body of the response.
   * @constructor
   */
  constructor(
    public readonly response: Response,
    public readonly body: T,
  ) {
    super(typeof body === 'string' ? body : response.statusText);
  }
}

const REQUEST_TIMEOUT = 408;
const TOO_EARLY = 425;
const TOO_MANY_REQUESTS = 429;
const INTERNAL_SERVER_ERROR = 500;
const BAD_GATEWAY = 502;
const SERVICE_UNAVAILABLE = 503;
const GATEWAY_TIMEOUT = 504;

const STATUS_CODES_TO_RETRY = [
  REQUEST_TIMEOUT,
  TOO_EARLY,
  TOO_MANY_REQUESTS,
  INTERNAL_SERVER_ERROR,
  BAD_GATEWAY,
  SERVICE_UNAVAILABLE,
  GATEWAY_TIMEOUT,
];

const DEFAULT_BACKOFF_OPTIONS: BackoffOptions = {
  numOfAttempts: 10,
  maxDelay: Infinity,
  jitter: 'none',
  retry: (error: Error | OrderBookApiError) => {
    if (error instanceof OrderBookApiError) {
      return STATUS_CODES_TO_RETRY.includes(error.response.status);
    }

    return true;
  },
};

const DEFAULT_LIMITER_OPTIONS: RateLimiterOpts = {
  tokensPerInterval: 5,
  interval: 'second',
};

interface FetchParams {
  path: string;
  method: 'GET' | 'POST' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: URLSearchParams;
}

const getResponseBody = async (response: Response): Promise<unknown> => {
  if (response.status !== 204) {
    const contentType = response.headers.get('Content-Type');
    if (contentType) {
      if (contentType.toLowerCase().startsWith('application/json')) {
        return await response.json();
      } else {
        return await response.text();
      }
    }
  }
  return undefined;
};

/**
 * Helper function to make a rate-limited request to an API.
 * @param baseUrl The base URL of the API.
 * @param path The path of the request.
 * @param query The query parameters of the request.
 * @param method The HTTP method of the request.
 * @param body The body of the request.
 * @param rateLimiter The rate limiter to use.
 * @param backoffOpts The backoff options to use.
 * @param signal The fetch abort signal to use.
 * @returns The response of the request.
 * @throws If the API returns an error or if the request fails.
 */
export const request = async <T>(
  baseUrl: string,
  { path, query, method, body }: FetchParams,
  rateLimiter: RateLimiter,
  backoffOpts: BackoffOptions,
  signal?: AbortSignal,
): Promise<T> => {
  const queryString = query ? '?' + query : '';
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  const url = `${baseUrl}${path}${queryString}`;
  const bodyContent = (() => {
    if (!body) return undefined;

    return typeof body === 'string' ? body : JSON.stringify(body);
  })();
  const init: RequestInit = {
    method,
    body: bodyContent,
    headers,
    signal,
  };

  return backOff<T>(async () => {
    await rateLimiter.removeTokens(1);

    const response = await fetch(url, init);
    const responseBody = (await getResponseBody(response)) as T;

    // Successful response
    if (response.status >= 200 && response.status < 300) {
      return responseBody;
    }

    return Promise.reject(new OrderBookApiError(response, JSON.stringify(responseBody)));
  }, backoffOpts);
};

export interface EnrichedOrder extends Order {
  totalFee: string;
}

const BUY_ETH_string = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';

/**
 * Apply programmatic transformations to an order.
 *
 * For example, transformations may be applied to an order to recognise it as a Native EthFlow order.
 * @param order to apply transformations to
 * @returns An order with the total fee added.
 */
export const transformOrder = (order: Order): EnrichedOrder => {
  return transformEthFlowOrder(addTotalFeeToOrder(order));
};

/**
 * Add the total fee to the order.
 *
 * The total fee of the order will be represented by the `totalFee` field, which is the sum of `executedSurplusFee`
 * and `executedFeeAmount`.
 *
 * Note that either `executedSurplusFee` or `executedFeeAmount` may be `0`, or both might have a non `0` value.
 *
 * See https://cowservices.slack.com/archives/C036G0J90BU/p1705322037866779?thread_ts=1705083817.684659&cid=C036G0J90BU
 *
 * @param dto The order to add the total fee to.
 * @returns The order with the total fee added.
 */
export const addTotalFeeToOrder = (dto: Order): EnrichedOrder => {
  const { executedFeeAmount, executedSurplusFee } = dto;

  const _executedFeeAmount = BigInt(executedFeeAmount || '0');
  const _executedSurplusFee = BigInt(executedSurplusFee || '0');

  const totalFee = String(_executedFeeAmount + _executedSurplusFee);

  return {
    ...dto,
    totalFee,
  };
};

/**
 * Transform order field for Native EthFlow orders
 *
 * A no-op for regular orders
 * For Native EthFlow, due to how the contract is setup:
 * - sellToken set to Native token string
 * - owner set to `onchainUser`
 * - validTo set to `ethflowData.userValidTo`
 */
const transformEthFlowOrder = (order: EnrichedOrder): EnrichedOrder => {
  const { ethflowData } = order;

  if (!ethflowData) {
    return order;
  }

  const { userValidTo: validTo } = ethflowData;
  const owner = order.onchainUser || order.owner;
  const sellToken = BUY_ETH_string;

  return { ...order, validTo, owner, sellToken };
};

/**
 * An object containing *production* environment base URLs for each supported `chainId`.
 * @see {@link https://api.cow.fi/docs/#/}
 */
export const ORDER_BOOK_PROD_CONFIG: ApiBaseUrls = {
  [SupportedChainId.MAINNET]: 'https://api.cow.fi/mainnet',
  [SupportedChainId.GNOSIS_CHAIN]: 'https://api.cow.fi/xdai',
  [SupportedChainId.ARBITRUM_ONE]: 'https://api.cow.fi/arbitrum_one',
  [SupportedChainId.SEPOLIA]: 'https://api.cow.fi/sepolia',
};

/**
 * An object containing *staging* environment base URLs for each supported `chainId`.
 */
export const ORDER_BOOK_STAGING_CONFIG: ApiBaseUrls = {
  [SupportedChainId.MAINNET]: 'https://barn.api.cow.fi/mainnet',
  [SupportedChainId.GNOSIS_CHAIN]: 'https://barn.api.cow.fi/xdai',
  [SupportedChainId.ARBITRUM_ONE]: 'https://barn.api.cow.fi/arbitrum_one',
  [SupportedChainId.SEPOLIA]: 'https://barn.api.cow.fi/sepolia',
};

/**
 * The parameters for the `getOrders` request.
 */
export type GetOrdersRequest = {
  owner: string;
  offset?: number;
  limit?: number;
};

/**
 * The CoW Protocol OrderBook API client.
 *
 * This is the main entry point for interacting with the CoW Protocol OrderBook API. The main advantage of using
 * this client is the batteries-included approach to interacting with the API. It handles:
 *
 * - Environment configuration (mainnet, staging, etc.)
 * - Rate limiting
 * - Retries
 * - Backoff
 * - Error handling
 * - Request signing
 * - Request validation
 *
 * @example
 *
 * ```typescript
 * import { OrderBookApi, OrderSigningUtils, SupportedChainId } from '...'
 * import { Web3Provider } from '@ethersproject/providers'
 *
 * const account = 'YOUR_WALLET_string'
 * const chainId = 100 // Gnosis chain
 * const provider = new Web3Provider(window.ethereum)
 * const signer = provider.getSigner()
 *
 * const quoteRequest = {
 *   sellToken: '0x6a023ccd1ff6f2045c3309768ead9e68f978f6e1', // WETH gnosis chain
 *   buyToken: '0x9c58bacc331c9aa871afd802db6379a98e80cedb', // GNO gnosis chain
 *   from: account,
 *   receiver: account,
 *   sellAmountBeforeFee: (0.4 * 10 ** 18).toString(), // 0.4 WETH
 *   kind: OrderQuoteSide.kind.SELL,
 * }
 *
 * const orderBookApi = new OrderBookApi({ chainId: SupportedChainId.GNOSIS_CHAIN })
 *
 * async function main() {
 *     const { quote } = await orderBookApi.getQuote(quoteRequest)
 *
 *     const orderSigningResult = await OrderSigningUtils.signOrder(quote, chainId, signer)
 *
 *     const orderId = await orderBookApi.sendOrder({ ...quote, ...orderSigningResult })
 *
 *     const order = await orderBookApi.getOrder(orderId)
 *
 *     const trades = await orderBookApi.getTrades({ orderId })
 *
 *     const orderCancellationSigningResult = await OrderSigningUtils.signOrderCancellations([orderId], chainId, signer)
 *
 *     const cancellationResult = await orderBookApi.sendSignedOrderCancellations({...orderCancellationSigningResult, orderUids: [orderId] })
 *
 *     console.log('Results: ', { orderId, order, trades, orderCancellationSigningResult, cancellationResult })
 * }
 * ```
 *
 * @see {@link Swagger documentation https://api.cow.fi/docs/#/}
 * @see {@link OrderBook API https://github.com/cowprotocol/services}
 */
export class OrderBookApi {
  public context: ApiContext;

  private rateLimiter: RateLimiter;

  /**
   * Creates a new instance of the CoW Protocol OrderBook API client.
   * @param context - The API context to use. If not provided, the default context will be used.
   */
  constructor(context: PartialApiContext = {}) {
    this.context = { ...DEFAULT_COW_API_CONTEXT, ...context };
    this.rateLimiter = new RateLimiter(context.limiterOpts || DEFAULT_LIMITER_OPTIONS);
  }

  /**
   * Get an order by its unique identifier, `orderUid`.
   * @param orderUid The unique identifier of the order.
   * @param contextOverride Optional context override for this request.
   * @returns The order matching the request.
   */
  getOrder(orderUid: UID, contextOverride: PartialApiContext = {}): Promise<EnrichedOrder> {
    return this.fetch<Order>(
      { path: `/api/v1/orders/${orderUid}`, method: 'GET' },
      contextOverride,
    ).then((order) => {
      return transformOrder(order);
    });
  }

  /**
   * Get a quote for an order.
   * This allows for the calculation of the total cost of an order, including fees, before signing and submitting.
   * @param requestBody The parameters for the order quote request.
   * @param contextOverride Optional context override for this request.
   * @returns A hydrated order matching the request ready to be signed.
   */
  getQuote(
    requestBody: OrderQuoteRequest,
    contextOverride: PartialApiContext = {},
  ): Promise<OrderQuoteResponse> {
    return this.fetch(
      { path: '/api/v1/quote', method: 'POST', body: requestBody },
      contextOverride,
    );
  }

  /**
   * Submit an order to the order book.
   * @param requestBody The signed order to be submitted.
   * @param contextOverride Optional context override for this request.
   * @returns The unique identifier of the order.
   */
  sendOrder(requestBody: OrderCreation, contextOverride: PartialApiContext = {}): Promise<UID> {
    return this.fetch(
      { path: '/api/v1/orders', method: 'POST', body: requestBody },
      contextOverride,
    );
  }

  /**
   * Apply an override to the context for a request.
   * @param contextOverride Optional context override for this request.
   * @returns New context with the override applied.
   */
  private getContextWithOverride(contextOverride: PartialApiContext = {}): ApiContext & {
    signal?: AbortSignal;
  } {
    return { ...this.context, ...contextOverride };
  }

  /**
   * Get the base URLs for the API endpoints given the environment.
   * @param env The environment to get the base URLs for.
   * @returns The base URLs for the API endpoints.
   */
  private getApiBaseUrls(env: CowEnv): ApiBaseUrls {
    if (this.context.baseUrls) return this.context.baseUrls;

    return env === 'prod' ? ORDER_BOOK_PROD_CONFIG : ORDER_BOOK_STAGING_CONFIG;
  }

  /**
   * Make a request to the API.
   * @param params The parameters for the request.
   * @param contextOverride Optional context override for this request.
   * @returns The response from the API.
   */
  private fetch<T>(params: FetchParams, contextOverride: PartialApiContext = {}): Promise<T> {
    const {
      chainId,
      env,
      backoffOpts: _backoffOpts,
      signal,
    } = this.getContextWithOverride(contextOverride);
    const baseUrl = this.getApiBaseUrls(env)[chainId];
    const backoffOpts = _backoffOpts || DEFAULT_BACKOFF_OPTIONS;
    const rateLimiter = contextOverride.limiterOpts
      ? new RateLimiter(contextOverride.limiterOpts)
      : this.rateLimiter;

    return request(baseUrl, params, rateLimiter, backoffOpts, signal);
  }
}
